import { identifyLandingAccount } from './landingExperimentAnalytics';
import { posthog } from 'posthog-js';
import type { PostHogConfig } from 'posthog-js';
import { users, UserType } from '@prisma/client';
import { getEnv } from './env';

/**
 * Replay starts disabled and is switched on only once authentication has
 * resolved — to a non-staff user or to nobody. The provider mounts before the
 * stored token is validated, so recording from init would snapshot a returning
 * admin's first screen before anything could stop it.
 *
 * Replay privacy: every input is masked, anything under `.ph-no-capture` is
 * replaced with a blank block, and text under `[data-ph-mask]` is starred out.
 * Tag a surface with one of those rather than loosening the defaults here.
 */
export const posthogOptions: Partial<PostHogConfig> = {
  api_host: getEnv('POSTHOG_HOST') || undefined,
  person_profiles: 'identified_only',
  disable_session_recording: true,
  session_recording: {
    maskAllInputs: true,
    maskTextSelector: '[data-ph-mask]',
  },
};

/**
 * Staff sessions are never recorded: an admin screen shows every client's
 * offers, contracts and contact details, and none of it informs a funnel.
 */
export function identifyAnalyticsUser(user: users): void {
  if (!posthog.__loaded) return;
  posthog.identify(user.id, {
    email: user.email ?? undefined,
    name: `${user.firstName} ${user.lastName}`.trim(),
    user_type: user.type,
  });
  if (user.type === UserType.ADMIN) {
    posthog.stopSessionRecording();
    return;
  }
  identifyLandingAccount(user.id);
  posthog.startSessionRecording();
}

/**
 * Called once the browser is known to be signed out. Unlinks it from the
 * previous person so the next login on a shared device starts clean, then
 * records the anonymous session. An anonymous visitor is never reset: that
 * would sever the session that followed them in from the marketing site.
 */
export function recordSignedOutSession(): void {
  if (!posthog.__loaded) return;
  if (posthog._isIdentified()) posthog.reset();
  posthog.startSessionRecording();
}
